import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { config } from '../../config';

// Async action to fetch activities with pagination
export const fetchActivities = createAsyncThunk(
    'activities/fetchActivities',
    async ({ cardId, isActivityDetails, page = 1, limit = 20 }, { rejectWithValue }) => {
        const params = new URLSearchParams();

        if (isActivityDetails !== undefined) {
            params.append('isActivityDetails', isActivityDetails);
        }
        params.append('page', page);
        params.append('limit', limit);

        try {
            const response = await fetch(
                `${config.API_URI}/v1/cards/${cardId}/activities?${params.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    cache: 'no-store', // bypass HTTP cache to avoid 304 timing races
                }
            );

            // If the server says "Not Modified", keep current state (soft success)
            if (response.status === 304) {
                return rejectWithValue({ notModified: true, status: 304 });
            }

            if (!response.ok) {
                return rejectWithValue({ message: 'Failed to fetch activities', status: response.status });
            }

            const data = await response.json();
            return { ...data, page };
        } catch (error) {
            return rejectWithValue({ message: error.message });
        }
    }
);

// Activities slice
const activitiesSlice = createSlice({
    name: 'activities',
    initialState: {
        activities: [],
        loading: false,
        loadingMore: false,
        error: null,
        lastRequestId: null, // track the latest request; only update state when it matches
        highlightTarget: null,
        hasMore: true,
        page: 1,
        total: 0,
    },
    reducers: {
        resetActivities: (state) => {
            state.activities = [];
            state.page = 1;
            state.hasMore = true;
            state.total = 0;
        },
        addCommentOptimistically: (state, action) => {
            const optimisticComment = {
                ...action.payload,
                isOptimistic: true,
                isPosting: action.payload.isPosting !== undefined ? action.payload.isPosting : true
            };
            state.activities.unshift(optimisticComment); // Add the new comment at the start of the activities array
        },
        updateCommentOptimistically: (state, action) => {
            const { id, updatedDetails } = action.payload;
            const existingActivity = state.activities.find(activity => activity._id === id);
            if (existingActivity) {
                existingActivity.details = updatedDetails; // Update the comment's details
                existingActivity.isUpdating = true; // Show updating state
            }
        },
        setCommentPostingState: (state, action) => {
            const { tempId, isPosting } = action.payload;
            const item = state.activities.find(a => a._tempId === tempId);
            if (item) {
                item.isPosting = isPosting;
            }
        },
        setCommentUpdatingState: (state, action) => {
            const { id, isUpdating } = action.payload;
            const existingActivity = state.activities.find(activity => activity._id === id);
            if (existingActivity) {
                existingActivity.isUpdating = isUpdating;
            }
        },
        deleteCommentOptimistically: (state, action) => {
            const id = action.payload;
            state.activities = state.activities.filter(activity => activity._id !== id); // Remove the comment from the array
        },
        addReplyOptimistically: (state, action) => {
            const { reply, parentCommentId } = action.payload;
            // Find the parent comment activity and add the reply to its repliesData
            const parentActivity = state.activities.find(activity =>
                activity.actionTypeId === parentCommentId && activity.actionType === 'ADDED_COMMENT'
            );
            if (parentActivity && parentActivity.commentData) {
                if (!parentActivity.commentData.repliesData) {
                    parentActivity.commentData.repliesData = [];
                }
                const optimisticReply = {
                    ...reply,
                    isOptimistic: true,
                    isPosting: true // Show posting state for reply
                };
                // Add new reply at the beginning (first position)
                parentActivity.commentData.repliesData.unshift(optimisticReply);
            }
        },

        updateReplyOptimistically: (state, action) => {
            const { replyId, updatedDetails, reactions, isUpdating } = action.payload;
            // Update reply in the repliesData of the parent comment
            state.activities.forEach(activity => {
                if (activity.commentData && activity.commentData.repliesData) {
                    const reply = activity.commentData.repliesData.find(r => r._id === replyId);
                    if (reply) {
                        if (updatedDetails !== undefined) {
                            reply.comment = updatedDetails; // Update the reply's content
                        }
                        if (reactions !== undefined) {
                            reply.reactions = reactions; // Update the reply's reactions
                        }
                        if (isUpdating !== undefined) {
                            reply.isUpdating = isUpdating; // Set updating state
                        }
                    }
                }
            });
        },

        deleteReplyOptimistically: (state, action) => {
            const replyId = action.payload;
            // Remove reply from the repliesData of the parent comment
            state.activities.forEach(activity => {
                if (activity.commentData && activity.commentData.repliesData) {
                    activity.commentData.repliesData = activity.commentData.repliesData.filter(
                        reply => reply._id !== replyId
                    );
                }
            });
        },
        finalizeCommentOptimistically: (state, action) => {
            const { tempId, commentId } = action.payload;
            const item = state.activities.find(a => a._tempId === tempId);
            if (item) {
                item.actionTypeId = commentId;
                item.isOptimistic = true; // keep pinned until server list includes it
            }
        },
        updateReactionOptimistically: (state, action) => {
            const { commentId, reactions } = action.payload;
            const existingActivity = state.activities.find(activity =>
                activity.actionTypeId === commentId && activity.actionType === 'ADDED_COMMENT'
            );
            if (existingActivity) {
                existingActivity.reactions = reactions; // Update the comment's reactions
            }
        },

        // ========== REAL-TIME OPTIMISTIC ACTIONS ==========

        // Add comment from real-time event
        optimisticAddComment: (state, action) => {
            const comment = action.payload;
            // Check if comment already exists (avoid duplicates)
            const exists = state.activities.some(a =>
                a.actionTypeId === comment._id || a._tempId === comment._tempId
            );
            if (!exists) {
                state.activities.unshift(comment);
            }
        },

        // Update comment from real-time event
        optimisticUpdateComment: (state, action) => {
            const { commentId, updates } = action.payload;
            const activity = state.activities.find(a =>
                a.actionTypeId === commentId || a._id === commentId
            );
            if (activity) {
                Object.assign(activity, updates);
                // Remove optimistic flags
                delete activity.isOptimistic;
                delete activity.isPosting;
                delete activity.isUpdating;
            }
        },

        // Delete comment from real-time event
        optimisticDeleteComment: (state, action) => {
            const commentId = action.payload;
            state.activities = state.activities.filter(a =>
                a.actionTypeId !== commentId && a._id !== commentId
            );
        },

        // Set activities from real-time event (replaces entire list)
        setActivitiesFromSocket: (state, action) => {
            const activities = action.payload;
            state.activities = activities;
        },

        // Add activity optimistically for card actions (priority, due date, checklist, etc.)
        addActivityOptimistically: (state, action) => {
            const { cardId, userId, actionType, details, user } = action.payload;
            
            const optimisticActivity = {
                _id: `temp-${Date.now()}-${Math.random()}`,
                _tempId: `temp-${Date.now()}-${Math.random()}`,
                card: cardId,
                initiator: user || { _id: userId },
                actionType: actionType,
                details: details,
                createdAt: new Date().toISOString(),
                isOptimistic: true,
                isPosting: true
            };

            state.activities.unshift(optimisticActivity);
        },

        // Highlighting helpers
        setHighlightTarget: (state, action) => {
            state.highlightTarget = action.payload; // { type: 'comment'|'reply', id: string }
        },
        clearHighlightTarget: (state) => {
            state.highlightTarget = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActivities.pending, (state, action) => {
                const isFirstPage = action.meta.arg.page === 1;
                if (isFirstPage) {
                    state.loading = true;
                } else {
                    state.loadingMore = true;
                }
                state.error = null;
                state.lastRequestId = action.meta.requestId;
            })
            .addCase(fetchActivities.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.lastRequestId) return;
                state.loading = false;
                state.loadingMore = false;

                const { activities: incoming = [], hasMore, total, page } = action.payload;
                const isFirstPage = page === 1;

                const optimistic = state.activities.filter(a => a && a.isOptimistic);

                // For optimistic comments, match by content instead of ID since server assigns new IDs
                const incomingCommentContents = new Set(
                    incoming
                        .filter(a => a.actionType === 'ADDED_COMMENT' && a.commentData?.comment)
                        .map(a => a.commentData.comment.trim())
                );

                const stillPending = optimistic.filter(o => {
                    if (o.actionType === 'ADDED_COMMENT' && o.commentData?.comment) {
                        // For comments, check if content matches any incoming comment
                        return !incomingCommentContents.has(o.commentData.comment.trim());
                    }
                    // For other types, use ID matching
                    const incomingIds = new Set(incoming.map(a => a?.actionTypeId || a?._id));
                    return !o?.actionTypeId || !incomingIds.has(o.actionTypeId);
                });

                // Clean up optimistic replies from commentData.repliesData
                const cleanedActivities = incoming.map(activity => {
                    if (activity.commentData && activity.commentData.repliesData) {
                        // Find any existing optimistic replies for this comment
                        const existingActivity = state.activities.find(a =>
                            a.actionTypeId === activity.actionTypeId && a.actionType === 'ADDED_COMMENT'
                        );

                        if (existingActivity && existingActivity.commentData && existingActivity.commentData.repliesData) {
                            // Get server reply contents for matching
                            const serverReplyContents = new Set(activity.commentData.repliesData.map(r => r.comment?.trim()));

                            // Keep only optimistic replies that don't match any server reply content
                            const stillPendingReplies = existingActivity.commentData.repliesData.filter(r =>
                                r.isOptimistic === true && !serverReplyContents.has(r.comment?.trim())
                            );

                            // Merge server replies with still-pending optimistic replies (optimistic first for newest-first order)
                            activity.commentData.repliesData = [...stillPendingReplies, ...activity.commentData.repliesData];
                        }
                    }
                    return activity;
                });

                if (isFirstPage) {
                    state.activities = [...stillPending, ...cleanedActivities];
                } else {
                    // Append new activities for pagination
                    state.activities = [...state.activities, ...cleanedActivities];
                }

                state.hasMore = hasMore;
                state.total = total;
                state.page = page;
            })
            .addCase(fetchActivities.rejected, (state, action) => {
                if (action.meta.requestId !== state.lastRequestId) return;
                state.loading = false;
                state.loadingMore = false;
                if (action.payload && action.payload.notModified) {
                    return; // keep existing activities; nothing changed
                }
                state.error = action.payload?.message || action.error?.message || 'Failed to fetch activities';
            });
    },
});

// Export actions for optimistic updates
export const {
    resetActivities,
    addCommentOptimistically,
    updateCommentOptimistically,
    deleteCommentOptimistically,
    addReplyOptimistically,
    updateReplyOptimistically,
    deleteReplyOptimistically,
    finalizeCommentOptimistically,
    updateReactionOptimistically,
    setCommentPostingState,
    setCommentUpdatingState,
    // Real-time optimistic actions
    optimisticAddComment,
    optimisticUpdateComment,
    optimisticDeleteComment,
    setActivitiesFromSocket,
    addActivityOptimistically,
    // Highlight helpers
    setHighlightTarget,
    clearHighlightTarget,
} = activitiesSlice.actions;

export default activitiesSlice.reducer;
